import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ICheckboxOption } from "src/core/components/custom-ui/form-checkbox-multiple";
import { StakeholderRoleEnum } from "src/core/constants/folder";
import { useFolder, useFolderCorporationId } from "src/core/contexts/FolderContext";
import { useFolderGraphSync } from "src/core/contexts/FolderGraphSyncContext";
import {
  AddStakeholderBasicFormValues,
  addStakeholderBasicSchema,
  defaultAddStakeholderBasicValues,
} from "src/core/forms/stakeholder/addStakeholderBasic.schema";
import { AddStakeholderMutationVariables, useAddStakeholderMutation } from "src/core/gql/graphql";
import { useToast } from "src/core/hooks/use-toast";
import { buildCreateStakeholderVariables } from "src/core/utils/stakeholders";

type UseAddStakeholderBasicFormProps = {
  setOpen: (open: boolean) => void;
  onCreateSuccess?: (createdPersonId: string) => void;
};

/**
 * Hook pour le formulaire de création basique d'un stakeholder (partie prenante).
 * Basique signifie qu'il n'y a que le rôle, le prénom et le nom.
 */
export function useAddStakeholderBasicForm({
  setOpen,
  onCreateSuccess,
}: UseAddStakeholderBasicFormProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { setSelectedTab, setOpenUpdateModal, setSelectedStakeholderForEdit, setIsFromBasicForm } =
    useFolderGraphSync();
  // Permet de récupérer l'id de la société du dossier
  const corporationId = useFolderCorporationId();
  const { mappedStakeholders, refetch } = useFolder();
  // Permet d'appeler la mutation de création d'un stakeholder
  const [addStakeholder] = useAddStakeholderMutation();

  // Permet de gérer le spinner au niveau du bouton de soumission
  const [isLoading, setIsLoading] = useState(false);

  // Permet de gérer les options du select de rôle
  const [roleOptions, setRoleOptions] = useState<ICheckboxOption[]>([]);

  // Référence pour stocker l'ID du stakeholder créé
  const createdStakeholderIdRef = useRef<string | null>(null);

  // useEffect pour détecter quand le nouveau stakeholder apparaît dans la liste
  useEffect(() => {
    if (createdStakeholderIdRef.current) {
      const stakeholder = mappedStakeholders.find((s) => s.id === createdStakeholderIdRef.current);
      if (stakeholder) {
        setSelectedStakeholderForEdit(stakeholder);
        setOpenUpdateModal(true);
        createdStakeholderIdRef.current = null;
        setIsFromBasicForm(true);
      }
    }
  }, [mappedStakeholders, setSelectedStakeholderForEdit, setOpenUpdateModal, setIsFromBasicForm]);

  // Formulaire de création d'un stakeholder
  const form = useForm<AddStakeholderBasicFormValues>({
    resolver: zodResolver(addStakeholderBasicSchema()),
    defaultValues: defaultAddStakeholderBasicValues,
  });

  const onSubmit = useCallback(
    async (data: AddStakeholderBasicFormValues) => {
      try {
        setIsLoading(true);

        if (!corporationId) {
          throw new Error("Aucun id de société trouvé");
        }

        const variables = buildCreateStakeholderVariables(
          corporationId,
          data
        ) as AddStakeholderMutationVariables;

        const result = await addStakeholder({ variables });
        setSelectedTab("stakeholders");
        toast({
          description: t("form.addStakeholderBasic.success"),
        });

        // Récupérer l'ID de la personne créée
        const createdPersonId = result.data?.createPerson_0?.id;

        if (createdPersonId && onCreateSuccess) {
          onCreateSuccess(createdPersonId);
        }

        // Réinitialiser le formulaire
        form.reset(defaultAddStakeholderBasicValues);
        setIsLoading(false);
        setOpen(false);
        if (createdPersonId) {
          // Stocker l'ID du stakeholder créé pour le détecter plus tard
          createdStakeholderIdRef.current = createdPersonId;
          await refetch();
        }
      } catch (error) {
        console.error("useAddStakeholderBasicForm::error", error);
        toast({
          description: t("form.addStakeholderBasic.fail"),
          variant: "destructive",
        });
        setIsLoading(false);
        setOpen(false);
      }
    },
    [
      toast,
      t,
      corporationId,
      addStakeholder,
      setOpen,
      onCreateSuccess,
      form,
      setSelectedTab,
      refetch,
    ]
  );

  useEffect(() => {
    const options: ICheckboxOption[] = Object.entries(StakeholderRoleEnum).map(([key, value]) => ({
      id: value,
      label: t(`enum.stakeholdersType.${key}`),
    }));
    setRoleOptions(options);
  }, [t]);

  return {
    form,
    onSubmit,
    isLoading,
    roleOptions,
  };
}
